import { Button } from "~/components/ui/button";
import {
  ChevronDown,
  LayoutGrid,
  LayoutList,
  Map,
  Building2,
  CircleX,
} from "lucide-react";
import { MultiSelect } from "~/components/blocks/MultiSelectDropdown/MultiSelectDropdown";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { AMENITIES, OFFICE_TYPES, filterOfficeTypesToFilterOptions } from "./constants";
import { ClientOnly } from "~/components/blocks/ClientOnly/client-only";
import AutoPlaces from "./AutoPlaces";
import { parseAutocompleteSearchLocation } from "~/lib/helpers/autoCompleteSearchHelper";
import { useLocation, useNavigate, useRevalidator, useSearchParams } from "@remix-run/react";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { Workspace } from "./interfaces";
import { cn } from "~/lib/utils";
import {
  formatAreaQueryCityParam,
  formatAreaQueryCountryParam,
  formatAreaQueryStateParam,
} from "~/lib/helpers/StringHelper";
import { navigateOrRevalidateLocationSearch } from "~/components/blocks/Geosearch/locationGeosearch";
import RangeSlider from "./RangeSlider";
import { useEffect, useMemo, useState } from "react";
import { OhButton } from "~/components/ui/custom-radix-components/button";


export async function loader({ params }: { params: any }) {
  const googleMapKey = process.env.PUBLIC_REMIX_GOOGLE_MAP;
  // Replace with your API endpoint

  return { googleMapKey: googleMapKey };
}


export default function SearchBar({
  toggleDrawer,
  setWidth,
  rightWidth,
  setDisplayMode,
  callGeoSearch,
  filterData,
  setFilterData,
  query = '',
  displayMode,
  workspace,
  setMapLoaded,
  className,
  recalibrate,
  setRecalibrate,
  maxPrice,
  areaSummary,
  disableRedirect = false,
  onLocationChange,
  isPropertyPage = false,
}: {
  toggleDrawer: VoidFunction;
  setWidth: Function;
  rightWidth: number;
  setDisplayMode: Function;
  callGeoSearch: Function,
  filterData: any,
  setFilterData: Function,
  query: string,
  displayMode: string,
  workspace: Workspace,
  setMapLoaded?: Function,
  className?: string,
  recalibrate?: boolean,
  setRecalibrate?: Function,
  maxPrice?: number
  areaSummary?: any
  disableRedirect?: boolean
  onLocationChange?: (params: Record<string, string>) => void
  isPropertyPage?: boolean
}) {

  const navigate = useNavigate();
  const routeLocation = useLocation();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();
  const { currencySymbol } = useCurrentCurrency();
  const [pricePopoverOpen, setPricePopoverOpen] = useState(false);
  const [deskPopoverOpen, setDeskPopoverOpen] = useState(false);
  /**
   * After "Select All", URL uses `workspaceType=All` (officeTypes in filterData stay []).
   * Track that so the dropdown shows every option checked until the user picks specific types.
   */
  const [officeTypesImplicitAll, setOfficeTypesImplicitAll] = useState(false);

  useEffect(() => {
    const workspaceTypeParam = searchParams.get("workspaceType");
    // Non-area search: URL `All` drives the dropdown. Area pages manage implicit-all locally
    // so clearing the dropdown can use `workspaceType=All` for the API without checking every box.
    if (!areaSummary) {
      setOfficeTypesImplicitAll(workspaceTypeParam === "All");
    }
  }, [searchParams, areaSummary]);
  const handleClearPrice = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    callGeoSearch({
      minPrice: '',
      maxPrice: '',
      priceTiers: 'reset',
    });

    setPricePopoverOpen(false);
  };

  const handleClearDesk = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    callGeoSearch({
      minPeople: '',
      maxPeople: '',
    });

    setDeskPopoverOpen(false);
  };


  const handleResetFilters = () => {
    // Reset local filter state
    setOfficeTypesImplicitAll(false);

    setFilterData((prev: any) => ({
      ...prev,
      officeTypes: [],
      price: [],
      deskRange: [],
    }));

    // Reset filters in URL / search
    callGeoSearch({
      workspaceType: "reset",
      minPrice: "",
      maxPrice: "",
      priceTiers: "reset",
      minPeople: "",
      maxPeople: "",
    });

    // Close any open popovers
    setPricePopoverOpen(false);
    setDeskPopoverOpen(false);
  };

  const officeTypesForMultiSelect = useMemo(() => {
    if (officeTypesImplicitAll) {
      return OFFICE_TYPES.map((o) => o.value);
    }
    const fromFilter = filterData?.officeTypes;
    if (Array.isArray(fromFilter) && fromFilter.length > 0) {
      return filterOfficeTypesToFilterOptions(fromFilter);
    }
    return [];
  }, [filterData?.officeTypes, officeTypesImplicitAll]);

  const allOfficeTypeValues = useMemo(
    () => OFFICE_TYPES.map((o) => o.value),
    [],
  );

  /** Area pages: once the user uses this dropdown, stop falling back to areaSummary.spaceType. */
  const officeTypeFilterTouchedParam = areaSummary
    ? { officeTypeFilterTouched: "1" }
    : {};

  const handleOfficeTypesChange = (value: string[]) => {
    const allSelected =
      allOfficeTypeValues.length > 0 &&
      value.length === allOfficeTypeValues.length &&
      allOfficeTypeValues.every((v) => value.includes(v));
    if (allSelected) {
      setOfficeTypesImplicitAll(true);
      setFilterData((prev: any) => ({ ...prev, officeTypes: [] }));
      callGeoSearch({
        workspaceType: "All",
        ...officeTypeFilterTouchedParam,
      });
    } else {
      setOfficeTypesImplicitAll(false);
      setFilterData((prev: any) => ({
        ...prev,
        officeTypes: filterOfficeTypesToFilterOptions(value),
      }));
      const workspaceType =
        value.length > 0
          ? value.join(",")
          : areaSummary
            ? "All"
            : "reset";
      callGeoSearch({
        workspaceType,
        ...officeTypeFilterTouchedParam,
      });
    }
  };

  const handleLocationSelect = (target: string, selectedAddress?: string) => {
    if (!target) return;

    if (setRecalibrate) {
      setRecalibrate(true);
    }

    // Area entity page: keep current route and apply location as query params (country, state, city).
    if (areaSummary) {
      const parsed = parseAutocompleteSearchLocation(target);
      if (parsed) {
        const payload: Record<string, string> = {
          fromSearchBar: "1",
          country: formatAreaQueryCountryParam(parsed.countryCode),
          scope: "",
        };
        if (parsed.state) {
          payload.state = formatAreaQueryStateParam(parsed.state);
        } else {
          payload.state = "reset";
        }
        if (parsed.city) {
          payload.city = formatAreaQueryCityParam(parsed.city);
        } else if (parsed.state) {
          payload.city = "reset";
        } else {
          payload.city = "reset";
        }
        if (parsed.latitude) {
          payload.latitude = parsed.latitude;
        } else {
          payload.latitude = "reset";
        }
        if (parsed.longitude) {
          payload.longitude = parsed.longitude;
        } else {
          payload.longitude = "reset";
        }
        if (parsed.text) {
          payload.text = parsed.text;
        } else {
          payload.text = "reset";
        }
        callGeoSearch(payload);
        onLocationChange?.(payload);
        return;
      }
    }

    navigateOrRevalidateLocationSearch({
      target,
      currentPathname: routeLocation.pathname,
      currentSearch: routeLocation.search,
      navigate: (to) => navigate(to),
      revalidate: () => revalidator.revalidate(),
    });
  };

  const handleChange = ({ min, max }: { min: number | null; max: number | null }) => {
    console.log('Range changed:', { min, max });
  };

  const handleApply = ({ min, max }: { min: number | null; max: number | null }) => {
    if ((min === null || min === undefined || min === 0) && (max === null || max === undefined || max === 0)) {
      setPricePopoverOpen(false);
      return;
    }
    callGeoSearch({ minPrice: min, maxPrice: max, priceTiers: "reset" });
    setPricePopoverOpen(false);
  };

  const handleDeskApply = ({ min, max }: { min: number | null; max: number | null }) => {
    if ((min === null || min === undefined || min === 0) && (max === null || max === undefined || max === 0)) {
      setDeskPopoverOpen(false);
      return;
    }
    callGeoSearch({ minPeople: min, maxPeople: max });
    setDeskPopoverOpen(false);
  };

  // Helper function to format number as currency (rounded to whole numbers)
  const formatCurrency = (value: number): string => {
    if (value >= 1000000000000) { // Trillion
      return `${Math.round(value / 1000000000000)}T`;
    }
    if (value >= 1000000) { // Million
      return `${Math.round(value / 1000000)}M`;
    }
    if (value >= 1000) { // Thousand
      return `${Math.round(value / 1000)}k`;
    }
    return value.toLocaleString();
  };

  // Get price button text
  const getPriceButtonText = (): string => {
    const price = filterData.price;
    if (
      !price ||
      price.length === 0 ||
      ((price[0] === null || price[0] === undefined || price[0] === 0) &&
        (price[1] === null || price[1] === undefined || price[1] === 0))
    ) {
      return 'Budget';
    }

    const minPrice = price[0];
    const maxPrice = price[1];
    const hasMin = minPrice !== null && minPrice !== undefined && minPrice !== 0;
    const hasMax = maxPrice !== null && maxPrice !== undefined && maxPrice !== 0;

    if (hasMin && hasMax) {
      return `${currencySymbol}${formatCurrency(minPrice)} - ${currencySymbol}${formatCurrency(maxPrice)}`;
    } else if (hasMin) {
      return `${currencySymbol}${formatCurrency(minPrice)}+`;
    } else if (hasMax) {
      return `Up to ${currencySymbol}${formatCurrency(maxPrice)}`;
    }

    return 'Price';
  };

  // Get desk button text
  const getDeskButtonText = (): string => {
    const desks = filterData.deskRange;
    if (
      !desks ||
      desks.length === 0 ||
      ((desks[0] === null || desks[0] === undefined || desks[0] === 0) &&
        (desks[1] === null || desks[1] === undefined || desks[1] === 0))
    ) {
      return 'No. of Desks';
    }

    const minDesk = desks[0];
    const maxDesk = desks[1];
    const hasMin = minDesk !== null && minDesk !== undefined && minDesk !== 0;
    const hasMax = maxDesk !== null && maxDesk !== undefined && maxDesk !== 0;

    if (hasMin && hasMax) {
      return `${minDesk} - ${maxDesk} desks`;
    } else if (hasMin) {
      return `${minDesk}+ desks`;
    } else if (hasMax) {
      return `Up to ${maxDesk} desks`;
    }

    return 'Desks';
  };

  const priceRangeLabel = "Budget";

  const showAmenitiesAndSale = !areaSummary && !isPropertyPage;
  const hasActiveFilters = useMemo(() => {
    const hasOfficeTypes =
      officeTypesImplicitAll ||
      (Array.isArray(filterData?.officeTypes) &&
        filterData.officeTypes.length > 0);

    const hasPrice =
      Array.isArray(filterData?.price) &&
      filterData.price.some(
        (value: number | null) => value !== null && value !== undefined && value !== 0
      );

    const hasDeskRange =
      Array.isArray(filterData?.deskRange) &&
      filterData.deskRange.some(
        (value: number | null) => value !== null && value !== undefined && value !== 0
      );

    const hasUrlPriceFilter =
      query?.includes("minPrice") || query?.includes("maxPrice");

    const hasUrlDeskFilter =
      query?.includes("minPeople") || query?.includes("maxPeople");

    return (
      hasOfficeTypes ||
      hasPrice ||
      hasDeskRange ||
      hasUrlPriceFilter ||
      hasUrlDeskFilter
    );
  }, [
    filterData?.officeTypes,
    filterData?.price,
    filterData?.deskRange,
    officeTypesImplicitAll,
    query,
  ]);



  return (
    <div
      className={cn(
        'h-16 overflow-y-hidden fixed py-4 bg-white flex items-center overflow-x-auto scrollbar-hide justify-between w-full z-[12] px-[14px] shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
        className
      )}
    >


      <div className='flex items-center flex-1'>
        {/* Location Input */}
        <div className="relative w-[400px] max-[1000px]:w-full min-h-10 flex">
          <div className="flex-1">
            <ClientOnly>
              {() => (
                <AutoPlaces
                  fixed
                  onLocationSelect={handleLocationSelect}
                  inputStyleClassName="
                    rounded-full
                    bg-oh-light-aqua
                    border-oh-primary
                    text-slate-800
                    focus-visible:ring-0
                    focus-visible:border-oh-primary"
                />
              )}
            </ClientOnly>
          </div>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-gray-200 mx-4 max-[1000px]:hidden" />

        <div className="flex gap-2 max-[1000px]:hidden">
          <div>
            <MultiSelect
              options={OFFICE_TYPES}
              onValueChange={handleOfficeTypesChange}
              defaultValue={officeTypesForMultiSelect}
              className="rounded-full"
            />
          </div>

          <Popover open={pricePopoverOpen} onOpenChange={setPricePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`px-4 rounded-full ${query?.includes('minPrice') || query?.includes('maxPrice')
                  ? 'bg-oh-black text-white hover:bg-oh-black hover:text-white'
                  : 'bg-white text-black hover:bg-white hover:text-black'
                  }`}
              >
                {getPriceButtonText()}

                {query?.includes('minPrice') || query?.includes('maxPrice') ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClearPrice}
                    className="flex items-center justify-center rounded-full hover:bg-white/20"
                  >
                    <CircleX className="h-6 w-6 text-white transition-transform duration-150 hover:scale-110 hover:opacity-80" />
                  </span>
                ) : (
                  <ChevronDown
                    className={cn(
                      "ml-2 h-4 w-4 shrink-0 transition-transform duration-200",
                      pricePopoverOpen && "rotate-180"
                    )}
                  />
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent className="z-50 w-[440px] bg-white border p-5 rounded-2xl mt-2">
              <RangeSlider
                defaultMin={0}
                defaultMax={maxPrice || 10000}
                maxLimit={999999999999999}
                initialValue={filterData.price}
                label={priceRangeLabel}
                prefix={currencySymbol}
                type={areaSummary || isPropertyPage ? "Desk Price" : "Price"}
                showApplyButton={true}
                onApply={handleApply}
                inputClassName="px-3 py-[10px] text-oh-black font-normal text-sm select-none"
                reset={() => {
                  callGeoSearch({
                    minPrice: '',
                    maxPrice: '',
                    priceTiers: 'reset',
                  });
                }}
              />
            </PopoverContent>
          </Popover>
          <Popover open={deskPopoverOpen} onOpenChange={setDeskPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`px-4 rounded-full ${query?.includes('minPeople') || query?.includes('maxPeople')
                  ? 'bg-oh-black text-white hover:bg-oh-black hover:text-white'
                  : 'bg-white text-black hover:bg-white hover:text-black'
                  }`}
              >
                {getDeskButtonText()}

                {query?.includes('minPeople') || query?.includes('maxPeople') ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClearDesk}
                    className="flex items-center justify-center rounded-full hover:bg-white/20"

                  >
                    <CircleX className="h-6 w-6 text-white transition-transform duration-150 hover:scale-110 hover:opacity-80" />
                  </span>
                ) : (
                  <ChevronDown
                    className={cn(
                      "ml-2 h-4 w-4 shrink-0 transition-transform duration-200",
                      deskPopoverOpen && "rotate-180"
                    )}
                  />
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent className="z-50 bg-white border p-5 rounded-lg mt-2">
              <div>
                <RangeSlider
                  defaultMin={0}
                  defaultMax={500}
                  maxLimit={999999999999999}
                  initialValue={filterData.deskRange}
                  label={"Desks (number of people)"}
                  prefix={""}
                  type="desk"
                  showApplyButton={true}
                  onApply={handleDeskApply}
                  reset={() => {
                    callGeoSearch({
                      minPeople: '',
                      maxPeople: '',
                    });
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetFilters}
              className="px-4 rounded-full font-semibold text-sm text-[#E86749] hover:bg-[#E867491A] hover:text-[#E86749]"
            >
              Reset all
            </Button>
          )}

          {showAmenitiesAndSale ? (
            <>
              <div className="h-8 w-px bg-gray-200 mx-2 mt-1 max-[1000px]:hidden" />

              <Button
                onClick={() => {
                  if (query?.includes('promo')) {
                    setFilterData({ ...filterData, sale: '' })
                    callGeoSearch({ promo: '' })
                  } else {
                    setFilterData({ ...filterData, sale: 'sale' })
                    callGeoSearch({ promo: 'sale' })
                  }
                }}
                variant="outline"
                className={`px-4 rounded-full ${query?.includes('promo')
                  ? 'bg-oh-secondary text-white hover:bg-oh-secondary hover:text-white'
                  : 'bg-white text-black hover:bg-gray-100 hover:text-black'
                  }`}
              >
                <Building2 className="mr-2 h-4 w-4 text-current" />
                On Sale
              </Button>
            </>
          ) : null}
          {/* Workspace Button */}
          {query?.includes('workspaceId') ? <OhButton variant="destructive" className='px-4'>{workspace?.name}</OhButton> : null}
        </div>
      </div>
      <div className='inline-flex items-center gap-1 rounded-2xl bg-gray-100 p-1.5 ml-2 dark:bg-zinc-800'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            if (rightWidth === 100) {
              if (window.innerWidth < 1000) {
                setWidth(0)
              } else {
                setWidth(45)
                setDisplayMode('grid')
              }
            } else {
              setDisplayMode('map')
              setWidth(100)
            }
            if (setMapLoaded) {
              setMapLoaded(true)
            }
          }}
          className={`h-10 w-10 rounded-2xl transition-all ${displayMode === 'map'
            ? 'bg-zinc-900 text-white hover:bg-zinc-800 hover:text-white'
            : 'text-zinc-600 hover:bg-transparent hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
            }`}
        >
          <Map className='h-5 w-5' aria-label='Map view' />
        </Button>

        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            setTimeout(() => {
              setWidth(45)
              setDisplayMode('grid')
            }, 500)
          }}
          className={`h-10 w-10 rounded-2xl transition-all ${displayMode === 'grid'
            ? 'bg-zinc-900 text-white hover:bg-zinc-800 hover:text-white'
            : 'text-zinc-600 hover:bg-transparent hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
            }`}
        >
          <LayoutGrid className='h-5 w-5' aria-label='Grid layout' />
        </Button>

        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            setWidth(45)
            setDisplayMode('list')
          }}
          className={`h-10 w-10 rounded-2xl transition-all ${displayMode === 'list'
            ? 'bg-zinc-900 text-white hover:bg-zinc-800 hover:text-white'
            : 'text-zinc-600 hover:bg-transparent hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
            }`}
        >
          <LayoutList className='h-5 w-5' aria-label='List layout' />
        </Button>
      </div>
    </div>
  );
}
